import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";

import { getSuppliers, deleteSupplier, searchSuppliers } from '../services/supplierService';

const ListSupplier = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await getSuppliers();
      
      let supplierData = [];
      if (response?.payload && Array.isArray(response.payload)) {
        supplierData = response.payload;
      } else if (Array.isArray(response)) {
        supplierData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        supplierData = response.data;
      }
      
      setSuppliers(supplierData);
      
    } catch (error) {
      console.error('❌ Error fetching suppliers:', error);
      // Only show error toast for actual API failures, not for empty results
      if (error.response && error.response.status !== 404) {
        setError('Failed to load suppliers. Please try again.');
        toast.error('Failed to load suppliers');
      } else {
        // Empty result is not an error
        setSuppliers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchSuppliers();
      return;
    }

    try {
      setSearchLoading(true);
      
      const response = await searchSuppliers({ name: searchTerm.trim() });
      
      let searchResults = [];
      if (response?.payload && Array.isArray(response.payload)) {
        searchResults = response.payload;
      } else if (Array.isArray(response)) {
        searchResults = response;
      }
      
      setSuppliers(searchResults);
      setPage(0); // Reset to first page after search
      
    } catch (error) {
      console.error('❌ Search error:', error);
      toast.error('Search failed. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleDelete = async (supplierId, supplierName) => {
    if (!window.confirm(`Are you sure you want to delete "${supplierName}"?`)) {
      return;
    }

    try {
      await deleteSupplier(supplierId);
      
      const updatedSuppliers = suppliers.filter(supplier => supplier.Id !== supplierId);
      setSuppliers(updatedSuppliers);
      
      // Reset page if current page would be empty after deletion
      const newSupplierCount = updatedSuppliers.length;
      const maxPage = Math.ceil(newSupplierCount / rowsPerPage) - 1;
      if (page > maxPage && maxPage >= 0) {
        setPage(maxPage);
      }
      
      toast.success('Supplier deleted successfully');
      
    } catch (error) {
      console.error('❌ Delete error:', error);
      toast.error('Failed to delete supplier');
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    if (!value.trim()) {
      fetchSuppliers();
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleAddSupplier = () => {
    navigate('/suppliers/add');
  };

  const handleEditSupplier = (supplierId) => {
    navigate(`/suppliers/edit/${supplierId}`);
  };

  const handleViewSupplier = (supplierId) => {
    navigate(`/suppliers/view/${supplierId}`);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (isActive) => {
    return (
      <Chip
        label={isActive ? t("active") : t("inactive")}
        color={isActive ? 'success' : 'default'}
        size="small"
        variant="outlined"
      />
    );
  };

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesFilter =
      filterType === 'all' ||
      (filterType === 'active' && supplier.IsActive !== false) ||
      (filterType === 'inactive' && supplier.IsActive === false);
    return matchesFilter;
  });

  // Calculate paginated suppliers
  const paginatedSuppliers = filteredSuppliers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* ✅ Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Box>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 0.5
            }}
          >
            {t("suppliers")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("manage_supplier_info")}
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddSupplier}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 3
          }}
        >
          {t("add_supplier")}
        </Button>
      </Box>

      {/* ✅ Search and Actions Section */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={t("search_suppliers_placeholder")}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        fetchSuppliers();
                      }}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t("filter")}</InputLabel>
              <Select
                value={filterType}
                label={t("filter")}
                onChange={(e) => setFilterType(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">{t("all_suppliers")}</MenuItem>
                <MenuItem value="active">{t("active_only")}</MenuItem>
                <MenuItem value="inactive">{t("inactive_only")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Chip
                label={`${filteredSuppliers.length} ${t("suppliers")}`}
                color="primary"
                variant="outlined"
                icon={<FilterListIcon />}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* ✅ Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* ✅ Suppliers Table */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading || searchLoading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            py: 8 
          }}>
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              {t("loading_suppliers")}
            </Typography>
          </Box>
        ) : filteredSuppliers.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            py: 8,
            textAlign: 'center'
          }}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm || filterType !== 'all' ? t("no_suppliers_found") : t("no_suppliers_yet")}
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              {searchTerm || filterType !== 'all'
                ? t("try_adjusting_search_criteria") 
                : t("start_by_adding_first_supplier")
              }
            </Typography>
            {!searchTerm && filterType === 'all' && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSupplier}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {t("add_first_supplier")}
              </Button>
            )}
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>{t("supplier_name")}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t("contact_info")}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t("address")}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t("status")}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>{t("actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSuppliers.map((supplier, index) => (
                    <TableRow 
                      key={supplier.Id || index}
                      hover
                      sx={{ 
                        '&:hover': { backgroundColor: 'action.hover' },
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {supplier.Name || supplier.SupplierName || 'N/A'}
                          </Typography>
                          {supplier.CompanyRegistrationNumber && (
                            <Typography variant="caption" color="text.secondary">
                              {t("registration_number")}: {supplier.CompanyRegistrationNumber}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          {supplier.Phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2">{supplier.Phone}</Typography>
                            </Box>
                          )}
                          {supplier.Email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2">{supplier.Email}</Typography>
                            </Box>
                          )}
                          {!supplier.Phone && !supplier.Email && (
                            <Typography variant="body2" color="text.disabled">
                              {t("no_contact_info")}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          {(supplier.Address || supplier.City || supplier.Country) ? (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                              <LocationIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.2 }} />
                              <Box>
                                {supplier.Address && (
                                  <Typography variant="body2">{supplier.Address}</Typography>
                                )}
                                {(supplier.City || supplier.Country) && (
                                  <Typography variant="caption" color="text.secondary">
                                    {[supplier.City, supplier.Country].filter(Boolean).join(', ')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              {t("no_address")}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        {getStatusChip(supplier.IsActive !== false)}
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title={t("view_details")}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewSupplier(supplier.Id)}
                              sx={{ color: 'info.main' }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title={t("edit_supplier")}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditSupplier(supplier.Id)}
                              sx={{ color: 'warning.main' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title={t("delete_supplier")}>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(supplier.Id, supplier.Name || supplier.SupplierName)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Component */}
            <TablePagination
              component="div"
              count={filteredSuppliers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50'
              }}
            />
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ListSupplier;